import React, { useEffect, useLayoutEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Button, Modal } from 'components';
import { useAuthStore } from 'stores/authStore';
import { useTranslation } from 'react-i18next';
import AUTH_KEYS from 'api/auth/auth.keys';
import { useDeferredLogin } from 'api/auth/hooks';
import { useMobileAppPromptModalStore } from 'stores/mobileAppPromptModalStore';
import { isMobilePhone } from 'utils/mobileDevice';

const DEFERRED_LOGIN_URL = 'https://rambla-test.godetour.link/1lzHCkBqUS';

const wasPromptShown = () => {
  if (typeof window === 'undefined') return true;

  try {
    return window.localStorage.getItem('rambla_mobile_app_prompt_shown') === 'true';
  } catch {
    return true;
  }
};

const setPromptShown = () => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem('rambla_mobile_app_prompt_shown', 'true');
  } catch {
    // Ignore storage errors; the prompt should not break app access.
  }
};

export default function MobileAppPromptModal() {
  const { t } = useTranslation(['app']);
  const queryClient = useQueryClient();
  const isOpen = useMobileAppPromptModalStore((state) => state.isOpen);
  const openMobileAppPromptModal = useMobileAppPromptModalStore(
    (state) => state.openMobileAppPromptModal,
  );
  const closeMobileAppPromptModal = useMobileAppPromptModalStore(
    (state) => state.closeMobileAppPromptModal,
  );
  const isUserLoggedIn = useAuthStore((state) => !!state.user);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const {
    data: deferredLogin,
    isFetching: isDeferredLoginFetching,
    refetch: fetchDeferredLogin,
  } = useDeferredLogin();

  useEffect(() => () => closeMobileAppPromptModal(), [closeMobileAppPromptModal]);

  useLayoutEffect(() => {
    if (!isUserLoggedIn || isImpersonating || wasPromptShown() || !isMobilePhone()) return;

    setPromptShown();
    openMobileAppPromptModal();
  }, [isImpersonating, isUserLoggedIn, openMobileAppPromptModal]);

  const handleClose = () => {
    queryClient.resetQueries({ queryKey: AUTH_KEYS.DEFERRED_LOGIN() });
    closeMobileAppPromptModal();
  };

  const deferredLoginName = [deferredLogin?.name, deferredLogin?.last_name]
    .filter(Boolean)
    .join(' ');
  const deferredLoginUrl = deferredLogin?.token
    ? `${DEFERRED_LOGIN_URL}?${new URLSearchParams({
        token: deferredLogin.token,
        ...(deferredLoginName ? { name: deferredLoginName } : {}),
      }).toString()}`
    : null;

  const handleStoreClick = async () => {
    if (deferredLoginUrl && deferredLogin?.code) {
      try {
        await navigator.clipboard?.writeText(deferredLogin.code);
      } catch {
        // Opening the app should still work if the browser blocks clipboard access.
      }
      return;
    }

    await fetchDeferredLogin();
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      closeOnDocumentClick={false}
      header={{ title: t('mobileAppPrompt.title') }}
    >
      <Content>
        <Description>{t('mobileAppPrompt.description')}</Description>

        {deferredLogin?.code && (
          <CodePrompt aria-live="polite">
            <CodePromptText>{t('mobileAppPrompt.rememberCode')}</CodePromptText>
            <DeferredLoginCode>{deferredLogin.code}</DeferredLoginCode>
          </CodePrompt>
        )}

        <Actions>
          <StoreButton
            as={deferredLoginUrl ? 'a' : undefined}
            href={deferredLoginUrl || undefined}
            target={deferredLoginUrl ? '_blank' : undefined}
            rel={deferredLoginUrl ? 'noopener noreferrer' : undefined}
            type={deferredLoginUrl ? undefined : 'button'}
            onClick={handleStoreClick}
            disabled={isDeferredLoginFetching}
            variant="primary"
          >
            {isDeferredLoginFetching
              ? t('mobileAppPrompt.loading')
              : t(deferredLoginUrl ? 'mobileAppPrompt.openApp' : 'mobileAppPrompt.generateCode')}
          </StoreButton>
        </Actions>
      </Content>
    </Modal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 300px;
  color: #000;
`;

const Description = styled.p`
  margin: 0;
  color: #6c757d;
  font-size: 16px;
`;

const CodePrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background-color: #f8f9fa;
`;

const CodePromptText = styled.p`
  margin: 0;
  color: #343a40;
  font-size: 14px;
  text-align: center;
`;

const DeferredLoginCode = styled.strong`
  color: #be1622;
  font-size: 36px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
`;

const StoreButton = styled(Button)`
  min-height: 44px;
  padding: 10px 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: #fff;
  color: #000;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover,
  &:focus-visible {
    background-color: #be1622;
    border-color: #be1622;
    color: #fff;
    text-decoration: none;
  }

  &:hover span,
  &:focus-visible span {
    color: #fff;
  }

  &:hover svg,
  &:focus-visible svg {
    color: #fff;
  }

  &:disabled,
  &:disabled:hover,
  &:disabled:focus-visible {
    background-color: #f8f9fa;
    border-color: #e9ecef;
    color: #6c757d;
    cursor: not-allowed;
  }

  &:disabled span,
  &:disabled svg {
    color: #6c757d;
  }

  &:active,
  &:active:focus {
    border-color: #be1622;
    color: #fff;
  }
`;
